import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";

const formatMoney = (value) => {
  const numberValue = Number(value ?? 0);
  return numberValue.toLocaleString("nl-NL", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatBytes = (value) => {
  const numberValue = Number(value ?? 0);
  if (numberValue < 1000) {
    return `${numberValue} B`;
  }
  if (numberValue < 1000 * 1000) {
    return `${(numberValue / 1000).toFixed(1)} KB`;
  }
  return `${(numberValue / (1000 * 1000)).toFixed(2)} MB`;
};

const getIssueSeverity = (severity) => String(severity ?? "").toLowerCase();

const amountsMatch = (left, right, tolerance = 0.01) => Math.abs(Number(left ?? 0) - Number(right ?? 0)) <= tolerance;

const isMissingFieldValue = (value) => {
  const normalized = String(value ?? "").trim();
  return !normalized || normalized.toLowerCase() === "missing";
};

const isProductCode10RequiredField = (fieldName) => {
  const normalized = String(fieldName ?? "").toLowerCase();
  return normalized === "freetext" || normalized === "language";
};

const isPackageAttachmentField = (fieldName) => String(fieldName ?? "").toLowerCase() === "packageattachment";

const isMissingAttachmentValue = (value) => {
  const normalized = String(value ?? "").trim().toLowerCase();
  return normalized === "missing from package" || normalized === "missing from blob storage";
};

const isPackageAttachmentMissingField = (field) =>
  Boolean(field) &&
  !field.isMatch &&
  (isPackageAttachmentField(field.fieldName) || isMissingAttachmentValue(field.xmlValue));

const getMissingAttachmentLabel = (fields = []) => {
  const missingField = fields.find((field) => isPackageAttachmentMissingField(field));
  if (String(missingField?.xmlValue ?? "").trim().toLowerCase() === "missing from blob storage") {
    return "Missing from blob";
  }

  return "Missing from package";
};

const getIssueDetailValue = (issue, key) => {
  const details = issue?.details ?? {};
  return details[key] ?? details[key.charAt(0).toUpperCase() + key.slice(1)] ?? "";
};

const getIssueInvoiceNumbers = (issue) => {
  const invoiceNumbers = [];
  const invoiceNumber = String(issue?.invoiceNumber ?? "").trim();
  if (invoiceNumber) {
    invoiceNumbers.push(invoiceNumber);
  }

  String(getIssueDetailValue(issue, "invoiceNumbers"))
    .split(",")
    .map((value) => value.trim())
    .filter(Boolean)
    .forEach((value) => invoiceNumbers.push(value));

  return invoiceNumbers.filter(
    (value, index, all) => all.findIndex((item) => item.toLowerCase() === value.toLowerCase()) === index
  );
};

const getFieldDisplayValue = (value) => (isMissingFieldValue(value) ? "-" : String(value).trim());

const getAttachmentFileNameDisplay = (fileName) => {
  const normalized = String(fileName ?? "").trim();
  return normalized || "-";
};

const getFieldStatusLabel = (field) => {
  if (isPackageAttachmentMissingField(field)) {
    return "Missing";
  }

  if (
    !field.isMatch &&
    isProductCode10RequiredField(field.fieldName) &&
    (isMissingFieldValue(field.sourceValue) || isMissingFieldValue(field.xmlValue))
  ) {
    return "Missing";
  }

  return field.isMatch ? "Match" : "Mismatch";
};

const getFieldStatusColor = (field) => {
  if (isPackageAttachmentMissingField(field)) {
    return "error";
  }

  if (
    !field.isMatch &&
    isProductCode10RequiredField(field.fieldName) &&
    (isMissingFieldValue(field.sourceValue) || isMissingFieldValue(field.xmlValue))
  ) {
    return "error";
  }

  return field.isMatch ? "success" : "error";
};

const ClaimXmlValidationDialog = ({
  open,
  validation,
  canSubmit,
  canDownload,
  onClose,
  onProceedAnyway,
  onDownload,
  onSubmit,
}) => {
  const [expandedInvoices, setExpandedInvoices] = useState({});
  const groupedInvoices = useMemo(() => {
    if (!validation?.invoiceComparisons) {
      return [];
    }

    const invoiceNumbersWithErrors = new Set();
    (validation.issues ?? []).forEach((issue) => {
      if (getIssueSeverity(issue.severity) !== "error") {
        return;
      }

      getIssueInvoiceNumbers(issue).forEach((invoiceNumber) => {
        invoiceNumbersWithErrors.add(invoiceNumber.toLowerCase());
      });
    });

    return validation.invoiceComparisons.map((invoice, index) => {
      const fields = invoice.fields ?? [];
      const fieldMismatchCount = fields.filter((field) => !field.isMatch).length;
      const hasInvoiceIssue = invoiceNumbersWithErrors.has(String(invoice.invoiceNumber ?? "").toLowerCase());
      const isPackageAttachmentMissing = fields.some((field) => isPackageAttachmentMissingField(field));

      return {
        key: `${invoice.customerInvoiceId || invoice.invoiceNumber || index}`,
        sequenceNumber: invoice.sequenceNumber,
        invoiceNumber: invoice.invoiceNumber,
        supplierName: invoice.supplierName,
        attachmentFileName: invoice.attachmentFileName ?? "",
        fields,
        mismatchCount: fieldMismatchCount || (hasInvoiceIssue ? 1 : 0),
        hasMismatch: fieldMismatchCount > 0 || hasInvoiceIssue,
        isPackageAttachmentMissing,
      };
    });
  }, [validation]);

  const errors = validation?.issues?.filter((issue) => getIssueSeverity(issue.severity) === "error") ?? [];
  const warnings = validation?.issues?.filter((issue) => getIssueSeverity(issue.severity) === "warning") ?? [];
  const summary = validation?.summary ?? {};
  const summaryRows = useMemo(() => {
    const hasTotalVatMismatch =
      !amountsMatch(summary.sourceTotalVatAmount, summary.xmlTotalVatAmount) ||
      !amountsMatch(summary.sourceTotalVatAmount, summary.claimTotalVatAmount) ||
      !amountsMatch(summary.xmlTotalVatAmount, summary.claimTotalVatAmount);
    const hasInvoiceCountMismatch = (summary.sourceInvoiceCount ?? 0) !== (summary.xmlInvoiceCount ?? 0);
    const hasAttachmentCountMismatch = (summary.xmlAttachmentCount ?? 0) !== (summary.packageAttachmentCount ?? 0);
    const hasAttachmentSizeMismatch = (summary.totalAttachmentSizeBytes ?? 0) > (summary.sizeLimitBytes ?? 0);

    return [
      {
        label: "Refund country",
        value: validation?.countryCode || "-",
        hasMismatch: false,
      },
      {
        label: "Total VAT",
        value: `Source ${formatMoney(summary.sourceTotalVatAmount)} / XML ${formatMoney(summary.xmlTotalVatAmount)} / Claim ${formatMoney(summary.claimTotalVatAmount)}`,
        hasMismatch: hasTotalVatMismatch,
      },
      ...(summary.msrDocumentsAvailable != null
        ? [
            {
              label: "MSR documents available",
              value: summary.msrDocumentsAvailable ? "Yes" : "No",
              hasMismatch: summary.msrDocumentsAvailable === false,
            },
          ]
        : []),
      {
        label: "Invoices",
        value: `Source ${summary.sourceInvoiceCount ?? 0} / XML ${summary.xmlInvoiceCount ?? 0}`,
        hasMismatch: hasInvoiceCountMismatch,
      },
      {
        label: "Attachments",
        value: `XML ${summary.xmlAttachmentCount ?? 0} / Package ${summary.packageAttachmentCount ?? 0}`,
        hasMismatch: hasAttachmentCountMismatch,
      },
      {
        label: "Attachment size",
        value: `${formatBytes(summary.totalAttachmentSizeBytes)} of ${formatBytes(summary.sizeLimitBytes)}`,
        hasMismatch: hasAttachmentSizeMismatch,
      },
    ];
  }, [summary, validation?.countryCode]);
  const showProceedAnyway = warnings.length > 0 && errors.length === 0;
  const handleToggleInvoice = (invoiceKey) => {
    setExpandedInvoices((current) => {
      const invoice = groupedInvoices.find((item) => item.key === invoiceKey);
      const currentValue = current[invoiceKey] ?? false;

      return {
        ...current,
        [invoiceKey]: !currentValue,
      };
    });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xl" aria-labelledby="claim-xml-validation-title">
      <DialogTitle id="claim-xml-validation-title">Claim XML validation</DialogTitle>
      <DialogContent>
        {!validation ? (
          <Alert severity="info">No validation result available yet.</Alert>
        ) : (
          <>
            <Box mb={3} sx={{ maxWidth: 520 }}>
              {summaryRows.map(({ label, value, hasMismatch }) => (
                <Box
                  key={label}
                  display="grid"
                  gridTemplateColumns="160px 1fr"
                  gap={2}
                  alignItems="baseline"
                  sx={{ py: 0.5 }}
                >
                  <Typography variant="subtitle2" color={hasMismatch ? "error" : "textPrimary"}>
                    {label}
                  </Typography>
                  <Typography>{value}</Typography>
                </Box>
              ))}
            </Box>

            {(errors.length > 0 || warnings.length > 0) && (
              <Box mb={3}>
                <Typography variant="h6" gutterBottom>
                  Validation issues
                </Typography>
                {validation.issues.map((issue, index) => (
                  <Alert
                    key={`${issue.message}-${issue.invoiceNumber ?? index}`}
                    severity={getIssueSeverity(issue.severity) === "error" ? "error" : "warning"}
                    sx={{ mb: 1 }}
                  >
                    <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
                      {getIssueInvoiceNumbers(issue).map((invoiceNumber) => (
                        <Chip key={invoiceNumber} size="small" label={`Invoice ${invoiceNumber}`} />
                      ))}
                      <Typography>{issue.message}</Typography>
                    </Box>
                  </Alert>
                ))}
              </Box>
            )}

            <Divider sx={{ mb: 2 }} />

            <Typography variant="h6" gutterBottom>
              Source vs XML
            </Typography>
            <Box sx={{ maxHeight: 520, overflow: "auto", border: "1px solid", borderColor: "divider" }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell />
                    <TableCell>#</TableCell>
                    <TableCell>Invoice</TableCell>
                    <TableCell>Attachment</TableCell>
                    <TableCell>Supplier</TableCell>
                    <TableCell>Mismatches</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {groupedInvoices.map((invoice) => {
                    const isExpanded = expandedInvoices[invoice.key] ?? false;

                    return (
                      <React.Fragment key={invoice.key}>
                        <TableRow
                          hover
                          sx={
                            invoice.hasMismatch
                              ? {
                                  bgcolor: (theme) => alpha(theme.palette.error.main, 0.12),
                                  "&:hover": {
                                    bgcolor: (theme) => alpha(theme.palette.error.main, 0.18),
                                  },
                                }
                              : undefined
                          }
                        >
                          <TableCell width={48}>
                            <IconButton
                              aria-label={isExpanded ? "Collapse invoice comparison" : "Expand invoice comparison"}
                              size="small"
                              onClick={() => handleToggleInvoice(invoice.key)}
                            >
                              {isExpanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
                            </IconButton>
                          </TableCell>
                          <TableCell>{invoice.sequenceNumber}</TableCell>
                          <TableCell sx={{ color: invoice.hasMismatch ? "error.main" : "inherit", fontWeight: invoice.hasMismatch ? 600 : 400 }}>
                            {invoice.invoiceNumber}
                          </TableCell>
                          <TableCell
                            sx={{
                              color: invoice.hasMismatch || invoice.isPackageAttachmentMissing ? "error.main" : "inherit",
                              maxWidth: 280,
                            }}
                            title={getAttachmentFileNameDisplay(invoice.attachmentFileName)}
                          >
                            <Box display="flex" alignItems="center" gap={1} sx={{ minWidth: 0 }}>
                              <Typography
                                component="span"
                                noWrap
                                sx={{
                                  fontSize: "inherit",
                                  fontWeight: invoice.isPackageAttachmentMissing ? 600 : 400,
                                }}
                              >
                                {getAttachmentFileNameDisplay(invoice.attachmentFileName)}
                              </Typography>
                              {invoice.isPackageAttachmentMissing && (
                                <Chip
                                  size="small"
                                  color="error"
                                  label={getMissingAttachmentLabel(invoice.fields)}
                                  aria-label={`Attachment ${getAttachmentFileNameDisplay(invoice.attachmentFileName)} is ${getMissingAttachmentLabel(invoice.fields).toLowerCase()}`}
                                />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell sx={{ color: invoice.hasMismatch ? "error.main" : "inherit" }}>
                            {invoice.supplierName}
                          </TableCell>
                          <TableCell sx={{ color: invoice.hasMismatch ? "error.main" : "inherit", fontWeight: invoice.hasMismatch ? 600 : 400 }}>
                            {invoice.mismatchCount}
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              color={invoice.hasMismatch ? "error" : "success"}
                              label={invoice.hasMismatch ? "Mismatch" : "Match"}
                            />
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell colSpan={7} sx={{ p: 0, borderBottom: isExpanded ? undefined : 0 }}>
                            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                              <Box sx={{ p: 2, backgroundColor: "background.default" }}>
                                <Table size="small" aria-label={`Invoice ${invoice.invoiceNumber} field comparison`}>
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>Field</TableCell>
                                      <TableCell>Source value</TableCell>
                                      <TableCell>XML value</TableCell>
                                      <TableCell>Status</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {invoice.fields.map((field, fieldIndex) => (
                                      <TableRow key={`${invoice.key}-${field.fieldName}-${fieldIndex}`}>
                                        <TableCell sx={{ color: field.isMatch ? "inherit" : "error.main" }}>
                                          {field.fieldName}
                                        </TableCell>
                                        <TableCell>{getFieldDisplayValue(field.sourceValue)}</TableCell>
                                        <TableCell>{getFieldDisplayValue(field.xmlValue)}</TableCell>
                                        <TableCell>
                                          <Chip
                                            size="small"
                                            color={getFieldStatusColor(field)}
                                            label={getFieldStatusLabel(field)}
                                          />
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })}
                  {groupedInvoices.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7}>No invoice comparison rows were returned.</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Close
        </Button>
        {showProceedAnyway && (
          <Button onClick={onProceedAnyway} color="warning" variant="contained">
            Proceed anyway
          </Button>
        )}
        <Button onClick={onDownload} color="primary" variant="contained" disabled={!canDownload}>
          Download claim XML
        </Button>
        {onSubmit && (
          <Button onClick={onSubmit} color="primary" variant="contained" disabled={!canSubmit}>
            Submit claim XML
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ClaimXmlValidationDialog;
